import { put, takeEvery, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  FetchUser,
  FetchUserNotificationsConfig,
  Logout,
  UpdateUserNotificationsConfig,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { ApiService, callSecureApi } from './api/callApi'
import { AccessInfo, User, UserNotificationsConfig } from 'src/interfaces/user'
import { UserNotificationPeriod } from 'src/constants/user'

function* onFetchUserNotificationsConfig(action: AsyncAction<void, UserNotificationsConfig>) {
  try {
    const { payload }: { payload: UserNotificationsConfig } = yield call(
      callSecureApi,
      Endpoints.FetchUserNotificationsConfig,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchUserNotificationsConfig.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch user notification config'

    yield put(FetchUserNotificationsConfig.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface UpdateUserNotificationsConfigPayload {
  enabled?: boolean
  contractorsOnboardedEnabled?: boolean
  contractorsOnboardedPeriod?: UserNotificationPeriod
  contractorsDaysBeforeDeactivatedEnabled?: boolean
  contractorsDaysBeforeDeactivatedPeriod?: UserNotificationPeriod
  contractorsDeactivatedEnabled?: boolean
  contractorsDeactivatedPeriod?: UserNotificationPeriod
  contractorsReactivatedEnabled?: boolean
  contractorsReactivatedPeriod?: UserNotificationPeriod
  paymentFailedToProcessEnabled?: boolean
  paymentFailedToProcessPeriod?: UserNotificationPeriod
  paymentFailedContractorComplianceEnabled?: boolean
  paymentFailedContractorCompliancePeriod?: UserNotificationPeriod
  paymentInGracePeriodEnabled: boolean
  paymentInGracePeriodPeriod: UserNotificationPeriod
}

function* onUpdateUserNotificationsConfig(
  action: AsyncAction<UpdateUserNotificationsConfigPayload>,
) {
  try {
    yield call(callSecureApi, Endpoints.UpdateUserNotificationsConfig, {
      apiService: ApiService.Paraworks,
      method: 'PUT',
      body: action.payload,
    })

    yield put(UpdateUserNotificationsConfig.success())
    yield put(FetchUserNotificationsConfig.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to update user notification config'

    yield put(UpdateUserNotificationsConfig.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchUser(action: AsyncAction<undefined, { user: User; accessInfo: AccessInfo }>) {
  try {
    const { payload }: { payload: { user: User; accessInfo: AccessInfo } } = yield call(
      callSecureApi,
      Endpoints.FetchUser,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchUser.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch user'

    yield put(FetchUser.failed(err))
    yield put(Logout.request())

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* userSaga() {
  yield takeEvery(FetchUser.type.REQUEST, onFetchUser)
  yield takeEvery(FetchUserNotificationsConfig.type.REQUEST, onFetchUserNotificationsConfig)
  yield takeEvery(UpdateUserNotificationsConfig.type.REQUEST, onUpdateUserNotificationsConfig)
}
